import React, { memo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import AutocompleteCDS from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import TextFieldCDS from '@sharedCR/coreDesignSystem/form/TextFieldCDS';
import { DeleteIcon, PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  CONSTRUCTION_BUDGET_CATEGORY_OPTIONS,
  CONSTRUCTION_TYPE_OPTIONS,
} from 'store/construction/types';

import { ConstructionTestIds } from '../../../Construction.testIds';

import {
  addLineButtonWrapperStyles,
  createFormWrapperStyles,
  createHeaderFieldsStyles,
  createLineFieldsStyles,
  createLineHeaderStyles,
  createLineRowStyles,
  createLinesWrapperStyles,
} from './BudgetCreateForm.styles';
import { getCategoryOption, getConstructionTypeOption } from './BudgetCreateForm.utils';

const BudgetCreateForm = ({
  draft,
  isRemoveDisabled,
  onConstructionTypeChange,
  onContingencyChange,
  makeLineCategoryChange,
  makeLineTextChange,
  makeRemoveLine,
  onAddLine,
}) => (
  <Box sx={createFormWrapperStyles}>
    <Box sx={createHeaderFieldsStyles}>
      <AutocompleteCDS
        dataTestId={ConstructionTestIds.BUDGET_CREATE_TYPE_FIELD}
        label="Construction Type"
        name="budget-create-construction-type"
        optionKey="id"
        options={CONSTRUCTION_TYPE_OPTIONS}
        value={getConstructionTypeOption(draft.constructionType)}
        onChange={onConstructionTypeChange}
      />
      <TextFieldCDS
        dataTestId={ConstructionTestIds.BUDGET_CREATE_CONTINGENCY_FIELD}
        label="Contingency Amount"
        name="budget-create-contingency"
        value={draft.contingencyAmount}
        onChange={onContingencyChange}
      />
    </Box>

    <Box sx={createLinesWrapperStyles}>
      {draft.lines.map((line, index) => (
        <Box key={line.key} sx={createLineRowStyles}>
          <Box sx={createLineHeaderStyles}>
            <Typography variant="medium14">{`Line ${index + 1}`}</Typography>
            <Button
              data-testid={ConstructionTestIds.BUDGET_CREATE_LINE_REMOVE_BUTTON}
              disabled={isRemoveDisabled}
              startIcon={<DeleteIcon size={IconSize[16]} />}
              variant="text"
              onClick={makeRemoveLine(line.key)}
            >
              Remove
            </Button>
          </Box>
          <Box sx={createLineFieldsStyles}>
            <AutocompleteCDS
              dataTestId={ConstructionTestIds.BUDGET_CREATE_LINE_CATEGORY_FIELD}
              label="Category"
              name={`budget-create-category-${line.key}`}
              optionKey="id"
              options={CONSTRUCTION_BUDGET_CATEGORY_OPTIONS}
              value={getCategoryOption(line.category)}
              onChange={makeLineCategoryChange(line.key)}
            />
            <TextFieldCDS
              dataTestId={ConstructionTestIds.BUDGET_CREATE_LINE_DESCRIPTION_FIELD}
              label="Description"
              name={`budget-create-description-${line.key}`}
              value={line.description}
              onChange={makeLineTextChange(line.key, 'description')}
            />
            <TextFieldCDS
              dataTestId={ConstructionTestIds.BUDGET_CREATE_LINE_AMOUNT_FIELD}
              label="Budgeted Amount"
              name={`budget-create-amount-${line.key}`}
              value={line.budgetedAmount}
              onChange={makeLineTextChange(line.key, 'budgetedAmount')}
            />
          </Box>
        </Box>
      ))}
    </Box>

    <Box sx={addLineButtonWrapperStyles}>
      <Button
        data-testid={ConstructionTestIds.BUDGET_CREATE_ADD_LINE_BUTTON}
        startIcon={<PlusIcon size={IconSize[16]} />}
        variant="outlined"
        onClick={onAddLine}
      >
        Add Line Item
      </Button>
    </Box>
  </Box>
);

BudgetCreateForm.propTypes = {
  draft: PropTypes.object.isRequired,
  makeLineCategoryChange: PropTypes.func.isRequired,
  makeLineTextChange: PropTypes.func.isRequired,
  makeRemoveLine: PropTypes.func.isRequired,
  onAddLine: PropTypes.func.isRequired,
  onConstructionTypeChange: PropTypes.func.isRequired,
  onContingencyChange: PropTypes.func.isRequired,
  isRemoveDisabled: PropTypes.bool,
};

export default memo(BudgetCreateForm);
